import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { isEmbeddedInIframe } from 'utils/utils';
import { TICKET_STATE_INVALIDATE_KEYS } from './ticketStateKeys';

/** What the Punchup host posts once a ticket action it ran for this list (Switch, Refund) has ended. */
const HOST_TICKET_REPLIES = ['switch-ticket-success', 'refund-ticket-success'] as const;
type HostTicketReply = typeof HOST_TICKET_REPLIES[number];

const isHostTicketReply = (action: unknown): action is HostTicketReply =>
	HOST_TICKET_REPLIES.some((reply) => reply === action);

/**
 * Refetches the attendee table when the Punchup host reports that a switch or a refund it ran for this list
 * has ended, so the rows show the change without a reload. Mount once where the table lives.
 *
 * Guards follow docs/iframe-communication.md → Inbound listener discipline: the page must be embedded, and the
 * message must come from the embedding host (`window.parent`), since any frame on the page can post one.
 */
export const useHostTicketBridge = (): void => {
	const queryClient = useQueryClient();

	useEffect(() => {
		if (!isEmbeddedInIframe()) return;

		const handler = (event: MessageEvent) => {
			if (event.source !== window.parent) return;
			if (!isHostTicketReply(event.data?.action)) return;
			// The same keys the page's own ticket mutations invalidate: the table, and an open ticket's details
			// and history.
			TICKET_STATE_INVALIDATE_KEYS.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
		};

		window.addEventListener('message', handler);
		return () => {
			window.removeEventListener('message', handler);
		};
	}, [queryClient]);
};
